import { getPropertyFullUrl } from '../../utils/formatters';
import { formatUnits } from '../../domain/property/formatters';
import { INQUIRY_PHONE } from '../../domain/inquiry/PHONE';
import { Property } from '../../services/api';
import {
  buildImageUrl,
  extractEyebrow,
  extractSeoDong,
  getTopicJosa,
  shortenAddress,
  SEO_CITY_PATTERN,
  SEO_GU_PATTERN,
} from './helpers';

// SEO data — 운영 페이지 voonang.com/property/2749 와 동일 형태 (사용자 피드백 2026-05-19)
export const buildSeoData = (property: Property | null) => {
  if (!property) return null;
  const baseUrl = 'https://voonang.com';
  const eyebrow = extractEyebrow(property.address);
  const address = property.address ?? '';

  // ── 운영 backend property_seo_view와 동등한 추출 패턴 ─────────
  const cityName = address.match(SEO_CITY_PATTERN)?.[1] ?? '';
  const guName = address.match(SEO_GU_PATTERN)?.[1] ?? '';
  const dong = extractSeoDong(address);
  const regionPrefix = [cityName, guName].filter(Boolean).join(' ');
  const addrShort = shortenAddress(address);
  const constructorName = property.constructor || '';
  const propertyType = (property as any).property_type || '아파트';

  // ── Title: 65자 초과 시 짧은 형식으로 폴백 (backend 동등) ─────
  let pageTitle = regionPrefix
    ? `${property.title} - ${regionPrefix} 분양정보 분양가 혜택 청약일정 | 부냥`
    : `${property.title} - 분양정보 분양가 혜택 청약일정 | 부냥`;
  if (pageTitle.length > 65) {
    pageTitle = regionPrefix
      ? `${property.title} | ${regionPrefix} 분양정보 | 부냥`
      : `${property.title} | 분양정보 | 부냥`;
  }

  // ── Description: 1순위 detailed_description, 2순위 자동 생성 ──
  // v2 입지 4카드 표식([교통]/[학군]/[생활]/[호재])은 검색 요약에 노출하지 않음 (대표 결정 2026-09-21 #3, 백엔드 동일)
  const rawDetail = ((property as any).detailed_description || '').replace(/\[(교통|학군|생활|호재)\]\s*/g, '');
  const cleanDetail = rawDetail.replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim();

  let pageDescription: string;
  if (cleanDetail.length > 50) {
    if (cleanDetail.length > 155) {
      const head = cleanDetail.slice(0, 155);
      const lastSpace = head.lastIndexOf(' ');
      pageDescription = (lastSpace > 0 ? head.slice(0, lastSpace) : head) + '...';
    } else {
      pageDescription = cleanDetail;
    }
  } else {
    const locText = dong || addrShort;
    const josa = getTopicJosa(property.title || '');
    const parts: string[] = [`${property.title}${josa} ${locText}에 위치`];
    if (constructorName) parts.push(`${constructorName} 시공`);
    if (property.total_units) parts.push(formatUnits(property));
    const intro =
      parts.length > 1
        ? `${parts[0]}한 ${parts.slice(1).join(', ')} ${propertyType}입니다.`
        : `${parts[0]}한 ${propertyType}입니다.`;

    const infoParts: string[] = [];
    if (property.min_area && property.max_area) {
      infoParts.push(`전용면적 ${property.min_area}~${property.max_area}㎡`);
    }
    const moveInRaw = String((property as any).move_in_date || '').trim();
    if (moveInRaw) {
      infoParts.push(
        moveInRaw.includes('완료') || moveInRaw.includes('예정')
          ? moveInRaw
          : `${moveInRaw} 입주예정`,
      );
    }

    pageDescription = intro + (infoParts.length ? ` ${infoParts.join(', ')}.` : '');
    if (pageDescription.length > 160) pageDescription = pageDescription.slice(0, 157) + '...';
  }

  // ── Keywords: dong/gu/city 단위로 단계적 확장 (backend 동등) ──
  const keywordList: string[] = [
    property.title,
    `${property.title} 분양`,
    `${property.title} 분양가`,
    `${property.title} 청약`,
    `${property.title} 모델하우스`,
    `${property.title} 혜택`,
  ];
  if (dong) {
    keywordList.push(`${dong} 분양`, `${dong} 아파트`);
  }
  if (guName) {
    keywordList.push(`${guName} 분양`, `${guName} 신규분양`);
  }
  if (cityName) {
    keywordList.push(`${cityName} 분양`, `${cityName} 아파트 분양`);
  }
  if (constructorName) keywordList.push(constructorName);
  if (property.developer) keywordList.push(property.developer);
  const keywords = keywordList.filter(Boolean).join(', ');

  const moveInText = String((property as any).move_in_date || '').trim();
  const fullUrl = getPropertyFullUrl(property, baseUrl);
  const imageUrl = buildImageUrl(property.image_url) ?? `${baseUrl}/og-default.jpg`;

  return {
    title: pageTitle,
    description: pageDescription,
    keywords,
    ogDescription: pageDescription,
    url: fullUrl,
    image: imageUrl,
    eyebrow,
    // JSON-LD: Apartment / RealEstateListing / BreadcrumbList / FAQPage / LocalBusiness
    // (Apartment 의 manufacturer 키는 backend property_seo_view 와 동등)
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'Apartment',
        name: property.title,
        description: pageDescription,
        url: fullUrl,
        image: imageUrl,
        address: {
          '@type': 'PostalAddress',
          streetAddress: property.address,
          addressCountry: 'KR',
          addressRegion: cityName || '대구',
        },
        ...(property.latitude && property.longitude
          ? { geo: { '@type': 'GeoCoordinates', latitude: property.latitude, longitude: property.longitude } }
          : {}),
        ...(constructorName
          ? { manufacturer: { '@type': 'Organization', name: constructorName } }
          : {}),
        numberOfRooms: property.total_units || undefined,
      },
      {
        '@context': 'https://schema.org',
        '@type': 'RealEstateListing',
        name: property.title,
        description: pageDescription,
        url: fullUrl,
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '부냥', item: baseUrl },
          ...(cityName
            ? [{ '@type': 'ListItem', position: 2, name: `${cityName} 분양`, item: `${baseUrl}/` }]
            : []),
          {
            '@type': 'ListItem',
            position: cityName ? 3 : 2,
            name: property.title,
            item: fullUrl,
          },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: `${property.title} 분양가는 얼마인가요?`,
            acceptedAnswer: {
              '@type': 'Answer',
              text: `${property.title}의 분양가는 현재 문의 중입니다. ${INQUIRY_PHONE}로 문의해 주세요.`,
            },
          },
          {
            '@type': 'Question',
            name: `${property.title} 입주는 언제인가요?`,
            acceptedAnswer: {
              '@type': 'Answer',
              text: `${property.title}의 입주는 ${moveInText || '미정'}입니다.`,
            },
          },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'LocalBusiness',
        name: `${property.title} 모델하우스`,
        description: `${property.title} 분양 모델하우스 — 분양가·공급일정·타입별 정보`,
        url: fullUrl,
        image: imageUrl,
        telephone: `+82-${INQUIRY_PHONE.replace(/-/g, '').replace(/^0/, '')}`,
        address: { '@type': 'PostalAddress', streetAddress: property.address, addressCountry: 'KR' },
        openingHoursSpecification: {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
          opens: '09:00',
          closes: '22:00',
        },
      },
    ],
  };
};

export type SeoData = NonNullable<ReturnType<typeof buildSeoData>>;
